import { ru } from './ru';

/**
 * Tarjima — KALIT SIFATIDA OʻZBEKCHA MATNNING OʻZI.
 *
 * NEGA `t('lesson.empty')` EMAS:
 *
 *  1. Kodda matn OʻQILADIGAN holda qoladi. Ramziy kalitlar bilan
 *     `t('profile.cert.modal.title')` nima yozilishini bilib boʻlmaydi —
 *     matnni koʻrish uchun boshqa faylga qarash kerak.
 *  2. Tarjimasi yoʻq satr OʻZBEKCHA chiqadi, `profile.cert.title` emas.
 *     Yaʼni yarim tarjima qilingan ilova ham ishlatsa boʻladi — bu
 *     bosqichma-bosqich koʻchirish uchun hal qiluvchi.
 *  3. ~1100 ta satrga kalit oʻylab chiqarish — ishning oʻzidan koʻra
 *     koʻproq vaqt oladi.
 *
 * Kamchiligi: oʻzbekcha matn oʻzgarsa, tarjima uziladi. Shuning uchun
 * `npm run tarjima` tarjimasiz satrlarni koʻrsatadi (CI da emas —
 * tarjima toʻliq boʻlmasligi xato emas, bu bosqichma-bosqich ish).
 *
 * Tashqi kutubxona olinmadi: i18next ~40 KB, bu esa mobil internetdagi
 * oʻquvchi uchun bu yerdagi butun mantiqdan qimmatroq.
 */

export type Language = 'uz' | 'ru';

/** Lug'atlar. Uzbek — manba til, shuning uchun lug'ati yo'q. */
const DICTIONARIES: Record<Language, Record<string, string | string[]>> = {
  uz: {},
  ru,
};

let currentLang: Language = 'uz';

export function setLanguage(language: Language): void {
  currentLang = language;
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

export function currentLanguage(): Language {
  return currentLang;
}

/**
 * Rus tilida koʻplik uch xil: 1 dars / 2 dars / 5 dars —
 * «1 урок», «2 урока», «5 уроков».
 *
 * Oʻzbek tilida bunday oʻzgarish yoʻq, shuning uchun bu faqat `ru`
 * uchun. Lugʻatda qiymat massiv boʻlsa — `[bir, ozgina, koʻp]`.
 */
function russianPluralForm(n: number): 0 | 1 | 2 {
  // Kasr son doim "few" shaklida: 2,5 балла, 16,7 балла.
  if (!Number.isInteger(n)) return 1;
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 0;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 1;
  return 2;
}

/**
 * Tarjima qiladi.
 *
 * @param text  Oʻzbekcha matn — u ham kalit, ham zaxira qiymat.
 * @param vars `{ count: 3 }` koʻrinishidagi qiymatlar. Matnda
 *               `{count}` deb yoziladi. `{count}` boʻlsa va lugʻatdagi
 *               qiymat massiv boʻlsa, koʻplik shakli tanlanadi.
 */
export function t(
  text: string,
  vars?: Record<string, string | number>,
): string {
  const dictionary = DICTIONARIES[currentLang];
  let result: string | string[] | undefined = dictionary?.[text];

  if (Array.isArray(result)) {
    // Koʻplik: birinchi sonli oʻrin boʻyicha tanlaymiz.
    const count = Object.values(vars || {}).find(
      (v) => typeof v === 'number',
    ) as number | undefined;
    result = result[count == null ? 0 : russianPluralForm(count)] ?? result[0];
  }

  let s = result ?? text;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}

/**
 * Telegram / brauzer tilidan boshlang'ich tilni aniqlaydi.
 *
 * Tartib: saqlangan tanlov -> Telegram `language_code` -> brauzer ->
 * oʻzbekcha. Rus tili faqat `ru` boʻlganda tanlanadi: qolgan hamma
 * holatda oʻzbekcha xavfsizroq zaxira, chunki markazlar oʻzbek
 * tilida.
 */
export function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem('shaftoli-til');
    if (saved === 'ru' || saved === 'uz') return saved;
  } catch {
    /* localStorage yopiq boʻlishi mumkin */
  }

  const tg = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user
    ?.language_code as string | undefined;
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';

  return (tg || browser || '').toLowerCase().startsWith('ru') ? 'ru' : 'uz';
}

export function saveLanguage(language: Language): void {
  try {
    localStorage.setItem('shaftoli-til', language);
  } catch {
    /* saqlanmasa ham ilova ishlayveradi */
  }
}
